import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { loadTrades, saveTrades, addTrade, deleteTrade, forceSync, onConnectivityChange, ensureFields, generateUID, setAuthToken, getAuthToken, clearAuthToken, startVersionPolling, stopVersionPolling, onTradesChanged, seedDemoData } from "./storage";
import { DEMO_TRADES } from "./demoData";

/* ─── TOTP Lock Screen ─── */
function LockScreen({ onUnlock }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isMobile = ('ontouchstart' in window || navigator.maxTouchPoints > 0) && window.innerWidth <= 480;
  const inputRef = useRef(null);

  async function handleSubmit(submittedCode) {
    const c = submittedCode || code;
    if (c.length !== 6) return;
    setLoading(true); setError("");
    // Demo: hardcoded code
    await new Promise(r => setTimeout(r, 400)); // simulate network delay
    if (c === "123124") {
      setAuthToken("demo");
      onUnlock();
    } else {
      setError("Invalid code. Try again.");
      setCode("");
      if (isMobile) inputRef.current?.focus();
    }
    setLoading(false);
  }

  useEffect(() => { if (code.length === 6) handleSubmit(code); }, [code]);

  // Desktop: global keyboard listener
  useEffect(() => {
    if (isMobile) return;
    function handleKeyDown(e) {
      if (loading) return;
      if (/^\d$/.test(e.key)) setCode(prev => prev.length < 6 ? prev + e.key : prev);
      else if (e.key === 'Backspace') setCode(prev => prev.slice(0, -1));
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, loading]);

  // Mobile: pin pad handlers
  function handleDigit(d) { if (!loading && code.length < 6) setCode(prev => prev + d); }
  function handleDelete() { setCode(prev => prev.slice(0, -1)); }

  return (
    <div style={{ fontFamily:"'IBM Plex Mono','Courier New',monospace", background:"#010409", minHeight:"100dvh", color:"#e6edf3", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{`
        .pin-btn { width:72px; height:72px; border-radius:50%; background:#0d1117; border:1px solid #21262d; color:#e6edf3; font-family:'Space Grotesk',sans-serif; font-size:22px; font-weight:600; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background 0.1s, border-color 0.1s; -webkit-tap-highlight-color:transparent; }
        .pin-btn:active { background:#21262d; border-color:#58a6ff44; }
      `}</style>
      <div style={{ textAlign:"center", maxWidth:340, width:"100%", padding:"0 24px" }}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12,marginBottom:6}}>
          <div style={{width:42,height:42,borderRadius:11,border:"1.5px solid #21262d",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <svg width={22} height={22} viewBox="0 0 28 24" fill="none" strokeLinecap="round">
              <line x1="6" y1="1" x2="6" y2="23" stroke="#00e5a088" strokeWidth="1.2"/>
              <rect x="3.5" y="5" width="5" height="9" rx="1" fill="#00e5a0" stroke="#00e5a066" strokeWidth="0.5"/>
              <line x1="14" y1="3" x2="14" y2="19" stroke="#ff5f5f88" strokeWidth="1.2"/>
              <rect x="11.5" y="7" width="5" height="7" rx="1" fill="#ff5f5f" stroke="#ff5f5f66" strokeWidth="0.5"/>
              <line x1="22" y1="2" x2="22" y2="22" stroke="#00e5a088" strokeWidth="1.2"/>
              <rect x="19.5" y="9" width="5" height="6" rx="1" fill="#00e5a0" stroke="#00e5a066" strokeWidth="0.5"/>
            </svg>
          </div>
          <div style={{ fontFamily:"'Plus Jakarta Sans','Space Grotesk',sans-serif", fontSize:24, fontWeight:800, letterSpacing:-0.5 }}>StrikeBook <span style={{fontWeight:600,color:"#484f58",marginLeft:4}}>Demo</span></div>
        </div>
        <div style={{ fontSize:10, color:isMobile?"#8b949e":"#484f58", letterSpacing:isMobile?2:1.5, marginBottom:isMobile?36:28 }}>{isMobile ? "AUTHENTICATOR CODE" : "Enter your 6-digit code to continue"}</div>

        <div style={{ display:"flex", justifyContent:"center", gap:8, marginBottom:20 }}>
          {[0,1,2,3,4,5].map(i => (
            <div key={i} style={{
              width: isMobile ? 44 : 40, height: isMobile ? 52 : 48, borderRadius:8, background:"#0d1117",
              border: `2px solid ${code.length === i ? "#58a6ff" : code[i] ? "#30363d" : "#21262d"}`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontFamily:"'Space Grotesk',sans-serif", fontSize: isMobile ? 24 : 22, fontWeight:700, color:"#e6edf3",
              transition:"border-color 0.15s",
            }}>{code[i] || ""}</div>
          ))}
        </div>

        {/* Desktop: loading state only */}
        {!isMobile && loading && (
          <div style={{ fontSize:12, color:"#8b949e", marginBottom:20, padding:"8px 0" }}>
            Verifying...
          </div>
        )}

        {/* Mobile: pin pad */}
        {isMobile && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, color:"#8b949e", marginBottom:16 }}>
              {loading ? "Verifying..." : "Enter your 6-digit code"}
            </div>
            {[[1,2,3],[4,5,6],[7,8,9],["",0,"⌫"]].map((row, ri) => (
              <div key={ri} style={{ display:"flex", justifyContent:"center", gap:12, marginBottom:12 }}>
                {row.map((digit, ci) => {
                  if (digit === "") return <div key={ci} style={{ width:72, height:72 }} />;
                  const isBackspace = digit === "⌫";
                  return (
                    <button key={ci} className="pin-btn"
                      onClick={() => isBackspace ? handleDelete() : handleDigit(String(digit))}
                      style={isBackspace ? { width:72, height:72, borderRadius:"50%", background:"transparent", border:"none", color:"#8b949e", fontFamily:"'Space Grotesk',sans-serif", fontSize:22, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" } : undefined}
                    >{digit}</button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div style={{ color:"#ff5f5f", fontSize:12, marginBottom:16, padding:"8px 12px", background:"#ff5f5f11", border:"1px solid #ff5f5f33", borderRadius:6 }}>{error}</div>
        )}
        <div style={{ fontSize:10, color:"#30363d", marginTop:24 }}>Code refreshes every 30 seconds</div>
      </div>
    </div>
  );
}

// ─── Default benchmark targets (overridable) ───
const DEFAULT_FLAT = 30;
const DEFAULT_CALL = 45;
const DEFAULT_PUT = 27;
const DEFAULT_BENCH = { flat: DEFAULT_FLAT, call: DEFAULT_CALL, put: DEFAULT_PUT };

function loadBenchmarkSettingsLocal() {
  try {
    const s = localStorage.getItem('ot_benchmarks');
    if (s) return JSON.parse(s);
  } catch(e) {}
  return DEFAULT_BENCH;
}

function saveBenchmarkSettingsLocal(settings) {
  try { localStorage.setItem('ot_benchmarks', JSON.stringify(settings)); } catch(e) {}
}

async function loadRemoteSettings() {
  // Demo: local only
  return { bench: loadBenchmarkSettingsLocal(), dashRange: loadDashRangeLocal() };
}

async function saveBenchmarkSettingsRemote(settings) {
  saveBenchmarkSettingsLocal(settings);
}

async function saveDashRangeRemote(range) {
  try { localStorage.setItem('ot_dash_range', range); } catch(e) {}
}

function loadDashRangeLocal() {
  try { return localStorage.getItem('ot_dash_range') || 'ALL'; } catch(e) { return 'ALL'; }
}

const RANGE_OPTIONS = [
  { key: '1D', label: '1D' },
  { key: '7D', label: '7D' },
  { key: '30D', label: '30D' },
  { key: '1Y', label: '1Y' },
  { key: 'ALL', label: 'ALL' },
];

function getDateCutoff(range) {
  if (range === 'ALL') return null;
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (range === '1D') return d.toISOString().slice(0, 10);
  if (range === '7D') { d.setDate(d.getDate() - 6); return d.toISOString().slice(0, 10); }
  if (range === '30D') { d.setDate(d.getDate() - 29); return d.toISOString().slice(0, 10); }
  if (range === '1Y') { d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10); }
  return null;
}

const emptyForm = { ticker:"", entryDate:"", exitDate:"", entryTime:"", exitTime:"", strike:"", type:"Call", expiration:"", entryPrice:"", exitPrice:"", entryStock:"", exitStock:"", contracts:"1", notes:"" };

// ─── Device layout hook ───
function useDeviceLayout() {
  const [layout, setLayout] = useState(() => detectLayout());
  function detectLayout() {
    const w = window.innerWidth, h = window.innerHeight;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isPhone = (w <= 480 && isTouchDevice) || (w <= 430 && h >= 700);
    const isTablet = !isPhone && w <= 1024 && isTouchDevice;
    const isMacBook = !isTouchDevice && w >= 1200 && w <= 1600 && h >= 750 && h <= 1000;
    const isLargeDesktop = !isTouchDevice && w > 1600;
    return {
      isPhone, isTablet, isMacBook, isLargeDesktop, isDesktop:!isPhone&&!isTablet, isPortrait:h>w, width:w, height:h,
      tickerCols: isPhone?2:isTablet?3:Math.min(6,Math.max(3,Math.floor(w/200))),
      statCols: isPhone?2:isTablet?2:4, formCols: isPhone?1:2, detailCols: isPhone?2:4,
      benchCols: isPhone?1:2, cpCols: isPhone?1:2,
      maxW: isLargeDesktop?1600:isMacBook?Math.min(w-80,1440):1100,
    };
  }
  useEffect(() => {
    const h = () => setLayout(detectLayout());
    window.addEventListener('resize', h);
    window.addEventListener('orientationchange', () => setTimeout(h, 100));
    return () => window.removeEventListener('resize', h);
  }, []);
  return layout;
}

// ─── Formatting ───
function fmtDate(d) {
  if (!d || d === "—") return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return d;
  return parsed.toISOString().slice(0, 10);
}

function fmtDateRange(entryDate, exitDate, entryTime, exitTime) {
  const timeStr = exitTime && exitTime !== "—" && exitTime !== "Expired"
    ? `${entryTime} → ${exitTime}`
    : exitTime === "Expired" ? `${entryTime} → Expired` : entryTime || "";
  let dayDiff = "";
  if (entryDate && exitDate && entryDate !== "—" && exitDate !== "—" && entryDate !== exitDate) {
    const d1 = new Date(entryDate), d2 = new Date(exitDate);
    const diff = Math.round((d2 - d1) / (1000*60*60*24));
    if (diff > 0) dayDiff = ` (+${diff})`;
  }
  return timeStr + dayDiff;
}

function addCommas(s) { const [int,dec]=s.split('.'); return int.replace(/\B(?=(\d{3})+(?!\d))/g,',')+( dec!==undefined?'.'+dec:''); }
function fmtMoney(v) { const n=Number(v); return isNaN(n)?"0.00":addCommas(Math.abs(n).toFixed(2)); }
function fmtPnl(v) { const n=Number(v); return isNaN(n)?"$0.00":`${n>=0?"+$":"-$"}${addCommas(Math.abs(n).toFixed(2))}`; }
function fmtPct(v) { const n=Number(v); return isNaN(n)?"0.00%":`${n>0?"+":""}${addCommas(n.toFixed(2))}%`; }

function PencilIcon({ size=14, color="#8b949e" }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:"block"}}><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>;
}

// ─── P&L ───
function calcPnl(ep, xp, c) {
  ep=Number(ep); xp=Number(xp); c=Math.max(1,Math.floor(Number(c)||1));
  if (isNaN(ep)||isNaN(xp)||ep===0) return {returnPct:0,pnl:0};
  return { returnPct:parseFloat((((xp-ep)/ep)*100).toFixed(2)), pnl:parseFloat(((xp-ep)*100*c).toFixed(2)) };
}

function normalizeTrade(t) {
  const c=Math.max(1,Math.floor(Number(t.contracts)||1));
  const {returnPct,pnl}=calcPnl(t.entryPrice,t.exitPrice,c);
  return { ...t, entryPrice:Number(t.entryPrice)||0, exitPrice:Number(t.exitPrice)||0, strike:Number(t.strike)||0,
    entryStock:Number(t.entryStock)||0, exitStock:Number(t.exitStock)||0, contracts:c,
    entryDate:fmtDate(t.entryDate), exitDate:fmtDate(t.exitDate), expiration:fmtDate(t.expiration), exitTime:t.exitTime||"",
    returnPct:(t.returnPct!==undefined&&t.returnPct!==0)?t.returnPct:returnPct,
    pnl:(t.pnl!==undefined&&t.pnl!==0)?t.pnl:pnl };
}

function calcMetrics(trades) {
  if (!trades.length) return {total:0,wins:0,losses:0,winRate:0,totalPnl:"0.00",avgPnl:"0.00",avgWin:"—",avgLoss:"—",avgReturn:"0.00"};
  const n=trades.map(normalizeTrade), w=n.filter(t=>t.pnl>0), l=n.filter(t=>t.pnl<0), tp=n.reduce((s,t)=>s+t.pnl,0);
  return { total:n.length, wins:w.length, losses:l.length, winRate:((w.length/n.length)*100).toFixed(0), totalPnl:tp.toFixed(2),
    avgPnl:(tp/n.length).toFixed(2), avgWin:w.length?(w.reduce((s,t)=>s+t.pnl,0)/w.length).toFixed(2):"—",
    avgLoss:l.length?(l.reduce((s,t)=>s+t.pnl,0)/l.length).toFixed(2):"—",
    avgReturn:(n.reduce((s,t)=>s+t.returnPct,0)/n.length).toFixed(2) };
}

function parseTimeToMinutes(t) {
  if (!t) return 0;
  const m=t.match(/(\d+):(\d+)(AM|PM)/i);
  if (!m) return 0;
  let h=parseInt(m[1]),min=parseInt(m[2]);
  if (m[3].toUpperCase()==="PM"&&h!==12) h+=12;
  if (m[3].toUpperCase()==="AM"&&h===12) h=0;
  return h*60+min;
}

function sortTrades(trades) {
  return [...trades].sort((a,b) => {
    const dA=a.entryDate||"",dB=b.entryDate||"";
    if (dA!==dB) return dB.localeCompare(dA);
    const tA=parseTimeToMinutes(a.entryTime),tB=parseTimeToMinutes(b.entryTime);
    if (tA!==tB) return tB-tA;
    return (b.updatedAt||0)-(a.updatedAt||0);
  });
}

const pnlColor = v => v>0?"#00e5a0":v<0?"#ff5f5f":"#8b949e";

/* ─── Trade Card ─── */
function TradeCard({ trade:rawTrade, onDelete, onEdit, L }) {
  const [expanded,setExpanded]=useState(false);
  const [confirmDelete,setConfirmDelete]=useState(false);
  const [deleting,setDeleting]=useState(false); // false | "deleting" | "deleted"
  const trade=normalizeTrade(rawTrade);
  const m=L.isPhone;

  async function handleConfirmDelete(e) {
    e.stopPropagation();
    if(deleting) return;
    setDeleting("deleting");
    try {
      await onDelete();
      setDeleting("deleted");
    } catch(err) {
      setDeleting(false);
    }
  }

  return (
    <div style={{ background:"#0d1117", borderBottom:"1px solid #161b22", padding:m?"14px 16px":"13px 18px" }}>
      <div onClick={()=>{setExpanded(!expanded);setConfirmDelete(false);}} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", cursor:"pointer" }}>
        {/* Left column: ticker+badge, strike, prices */}
        <div style={{ display:"flex", flexDirection:"column", gap:2, flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:m?6:10, flexWrap:"wrap" }}>
            <span style={{ fontWeight:700, fontSize:m?15:14, color:"#e6edf3", fontFamily:"'Space Grotesk',sans-serif" }}>{trade.ticker}</span>
            <span style={{ color:trade.type==="Call"?"#58a6ff":"#c084fc", fontSize:10, fontWeight:700, letterSpacing:0.5, background:(trade.type==="Call"?"#58a6ff":"#c084fc")+"18", padding:"3px 7px", borderRadius:4 }}>{trade.type.toUpperCase()}</span>
            {trade.contracts>1 && <span style={{ color:"#58a6ff", fontSize:m?10:9, fontWeight:600, opacity:0.7 }}>x{trade.contracts}</span>}
          </div>
          <span style={{ color:"#8b949e", fontSize:11 }}>${trade.strike}</span>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <span style={{ color:"#8b949e", fontSize:m?11:10 }}>${fmtMoney(trade.entryPrice)} → ${fmtMoney(trade.exitPrice)}</span>
            {trade.notes && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#58a6ff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0.45,flexShrink:0}}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>}
          </div>
        </div>
        {/* Right column: P&L, date, time range, expand */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:2, flexShrink:0, marginLeft:8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:m?6:10 }}>
            <span style={{ color:pnlColor(trade.returnPct), fontWeight:700, fontSize:13, fontFamily:"'Space Grotesk',sans-serif" }}>{fmtPct(trade.returnPct)}</span>
            <span style={{ color:pnlColor(trade.pnl), fontWeight:700, fontSize:13, fontFamily:"'Space Grotesk',sans-serif" }}>{fmtPnl(trade.pnl)}</span>
          </div>
          <span style={{ color:"#8b949e", fontSize:10 }}>{(()=>{const d=new Date(trade.entryDate);return isNaN(d)?trade.entryDate:d.toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"});})()}</span>
          <span style={{ color:"#8b949e", fontSize:10 }}>{fmtDateRange(trade.entryDate, trade.exitDate, trade.entryTime, trade.exitTime)}</span>
          <span style={{ color:"#30363d", fontSize:9, letterSpacing:0.3 }}>{expanded?"▴ collapse":"▾ expand"}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #21262d", position:"relative" }}>
          <button onClick={e=>{e.stopPropagation();onEdit(rawTrade);}} style={{ position:"absolute",top:12,right:0,background:"none",border:"1px solid #21262d",borderRadius:6,padding:m?"6px 8px":"4px 6px",cursor:"pointer",display:"flex",alignItems:"center",gap:4,opacity:0.6,transition:"opacity 0.15s" }}
            onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.6"} title="Edit trade">
            <PencilIcon size={m?14:12} color="#8b949e" /><span style={{ fontSize:m?10:9, color:"#8b949e", fontWeight:600, fontFamily:"inherit", letterSpacing:0.5 }}>EDIT</span>
          </button>
          <div style={{ display:"grid", gridTemplateColumns:`repeat(${L.detailCols},1fr)`, gap:m?14:12, marginBottom:10, paddingRight:m?70:60 }}>
            {[["Entry Time",trade.entryTime],["Exit Time",trade.exitTime||"—"],["Entry Stock",`$${fmtMoney(trade.entryStock)}`],["Exit Stock",`$${fmtMoney(trade.exitStock)}`],
              ["Entry Premium",`$${fmtMoney(trade.entryPrice)}`],["Exit Premium",`$${fmtMoney(trade.exitPrice)}`],["Exit Date",trade.exitDate||"—"],["Expiration",trade.expiration]
            ].map(([l,v])=>(
              <div key={l}><div style={{ fontSize:m?10:9, color:"#8b949e", letterSpacing:0.5, textTransform:"uppercase", marginBottom:3 }}>{l}</div>
              <div style={{ fontSize:m?13:12, color:"#e6edf3", fontFamily:"'Space Grotesk',sans-serif", fontWeight:500 }}>{v}</div></div>
            ))}
          </div>
          {trade.notes && <div style={{ marginTop:10, padding:"10px 12px", background:"#010409", borderRadius:6, border:"1px solid #21262d" }}>
            <div style={{ fontSize:m?10:9, color:"#8b949e", letterSpacing:0.5, textTransform:"uppercase", marginBottom:4 }}>NOTES</div>
            <div style={{ fontSize:m?12:11, color:"#c9d1d9", lineHeight:1.6 }}>{trade.notes}</div>
          </div>}
          <div style={{ marginTop:14, display:"flex", justifyContent:"flex-end" }}>
            {!confirmDelete ? (
              <button onClick={e=>{e.stopPropagation();setConfirmDelete(true);}} style={{ background:"none",border:"1px solid #30363d",borderRadius:6,color:"#8b949e",fontSize:m?12:10,fontWeight:600,fontFamily:"'Space Grotesk',sans-serif",padding:m?"8px 16px":"5px 12px",cursor:"pointer",transition:"all 0.15s" }}>DELETE TRADE</button>
            ) : (
              <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                <span style={{ fontSize:m?12:10, color:"#ff5f5f" }}>Sure?</span>
                <button onClick={handleConfirmDelete} disabled={!!deleting} style={{
                  background:deleting==="deleted"?"#ff5f5f":"#ff5f5f22",
                  border:`1px solid ${deleting==="deleted"?"#ff5f5f":"#ff5f5f44"}`,
                  borderRadius:6,color:deleting==="deleted"?"#010409":"#ff5f5f",
                  fontSize:m?12:10,fontWeight:700,fontFamily:"'Space Grotesk',sans-serif",
                  padding:m?"8px 16px":"5px 12px",cursor:deleting?"default":"pointer",
                  transition:"all 0.2s ease",
                  transform:deleting==="deleted"?"scale(1.02)":"scale(1)",
                  minWidth:m?100:80,
                }}>{deleting==="deleting"?"DELETING...":deleting==="deleted"?"✓ DELETED":"YES, DELETE"}</button>
                <button onClick={e=>{e.stopPropagation();setConfirmDelete(false);}} disabled={!!deleting} style={{ background:"none",border:"1px solid #30363d",borderRadius:6,color:"#8b949e",fontSize:m?12:10,fontWeight:600,fontFamily:"'Space Grotesk',sans-serif",padding:m?"8px 16px":"5px 12px",cursor:deleting?"default":"pointer",opacity:deleting?0.5:1,transition:"opacity 0.15s" }}>CANCEL</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Main App ─── */
export default function App() {
  const [unlocked, setUnlocked] = useState(() => !!getAuthToken());
  const L = useDeviceLayout();
  const m = L.isPhone;

  const [trades,setTrades]=useState([]);
  const [loaded,setLoaded]=useState(false);
  const [saveStatus,setSaveStatus]=useState(null);
  const [online,setOnline]=useState(navigator.onLine);
  const [syncing,setSyncing]=useState(false);
  const [form,setForm]=useState(emptyForm);
  const [editingUid,setEditingUid]=useState(null);
  const [activeTab,setActiveTab]=useState("dashboard");
  const [filterTicker,setFilterTicker]=useState("ALL");
  const [filterType,setFilterType]=useState("ALL");
  const [tickerSubview,setTickerSubview]=useState(null);
  const [benchSubview,setBenchSubview]=useState(null);
  const [saving,setSaving]=useState(false);
  const [benchSettings,setBenchSettings]=useState(()=>loadBenchmarkSettingsLocal());
  const [editingBench,setEditingBench]=useState(null);
  const [benchDraft,setBenchDraft]=useState({});
  const [dashRange,setDashRange]=useState(()=>loadDashRangeLocal());
  const [tickerExpanded,setTickerExpanded]=useState(false);
  const [filterTickerExpanded,setFilterTickerExpanded]=useState(false);
  const [tradePage,setTradePage]=useState(0);
  const [advancedOpen,setAdvancedOpen]=useState(false);
  const PER_PAGE = m ? 20 : 25;
  function goToPage(p){ setTradePage(p); setHeaderVisible(true); window.scrollTo({top:0,behavior:'smooth'}); }
  // Reset pagination when filters, tab, or subview changes
  useEffect(()=>{ setTradePage(0); },[activeTab,filterTicker,filterType,tickerSubview,benchSubview,dashRange]);

  // Sticky header: hide on scroll down, show on scroll up (mobile only)
  const [headerVisible,setHeaderVisible]=useState(true);
  const lastScrollY=useRef(0);
  useEffect(()=>{
    if(!m) return;
    const onScroll=()=>{
      const y=window.scrollY;
      if(y<50){setHeaderVisible(true);}
      else if(y>lastScrollY.current+20){setHeaderVisible(false);}
      else if(y<lastScrollY.current-10){setHeaderVisible(true);}
      lastScrollY.current=y;
    };
    window.addEventListener('scroll',onScroll,{passive:true});
    return ()=>window.removeEventListener('scroll',onScroll);
  },[m]);

  const FLAT_TARGET = benchSettings.flat;
  const CALL_TARGET = benchSettings.call;
  const PUT_TARGET = benchSettings.put;

  const handleAuthExpired = useCallback(()=>{ clearAuthToken(); setUnlocked(false); },[]);

  const loadTradesFromDB = useCallback(async()=>{
    try { const saved=await loadTrades(); if(saved?.length>0) setTrades(saved); } 
    catch(e){ if(e.message==='Unauthorized') handleAuthExpired(); else console.warn("Refresh failed:",e.message); }
  },[handleAuthExpired]);

  const syncAndRefresh = useCallback(async()=>{
    await loadTradesFromDB();
  },[loadTradesFromDB]);

  // Load on mount after unlock — seed demo data on first run
  useEffect(()=>{
    if(!unlocked) return;
    (async()=>{
      await seedDemoData(DEMO_TRADES);
      const saved=await loadTrades();
      if(saved?.length>0) setTrades(saved);
      setLoaded(true);
      const remote = await loadRemoteSettings();
      if(remote.bench) setBenchSettings(remote.bench);
      if(remote.dashRange) setDashRange(remote.dashRange);
    })();
  },[unlocked]);

  // No version polling in demo

  // Online status
  useEffect(()=>onConnectivityChange(setOnline),[]);

  // On visibility change — reload from IndexedDB
  useEffect(()=>{
    const handleVisible=()=>{ if(document.visibilityState==='visible') loadTradesFromDB(); };
    document.addEventListener('visibilitychange',handleVisible);
    return ()=>document.removeEventListener('visibilitychange',handleVisible);
  },[loadTradesFromDB]);
  useEffect(()=>{document.body.style.backgroundColor="#010409";document.documentElement.style.backgroundColor="#010409";requestAnimationFrame(()=>{document.body.style.opacity="1";});},[]);

  const allTrades=useMemo(()=>trades.map(normalizeTrade),[trades]);
  const dateCutoff=useMemo(()=>getDateCutoff(dashRange),[dashRange]);
  const rangedTrades=useMemo(()=>dateCutoff?allTrades.filter(t=>t.entryDate>=dateCutoff):allTrades,[allTrades,dateCutoff]);
  const metrics=useMemo(()=>calcMetrics(rangedTrades),[rangedTrades]);
  const tickers=useMemo(()=>{
    const counts={};allTrades.forEach(t=>{counts[t.ticker]=(counts[t.ticker]||0)+1;});
    const sorted=Object.keys(counts).sort((a,b)=>counts[b]-counts[a]);
    return ["ALL",...sorted];
  },[allTrades]);
  const filtered=useMemo(()=>sortTrades(allTrades.filter(t=>(filterTicker==="ALL"||t.ticker===filterTicker)&&(filterType==="ALL"||t.type===filterType))),[allTrades,filterTicker,filterType]);
  const tickerSubTrades=useMemo(()=>tickerSubview?sortTrades(rangedTrades.filter(t=>t.ticker===tickerSubview)):[], [rangedTrades,tickerSubview]);

  const benchSubTrades=useMemo(()=>{
    if(!benchSubview) return [];
    return sortTrades(rangedTrades.filter(t=>{
      const pnl=parseFloat(t.pnl), ret=parseFloat(t.returnPct);
      if(benchSubview.category==="loss") return pnl<0;
      if(pnl<=0) return false;
      if(benchSubview.bmType==="flat"){ return benchSubview.category==="hit"?ret>=FLAT_TARGET:ret<FLAT_TARGET; }
      else { const tgt=t.type==="Call"?CALL_TARGET:PUT_TARGET; return benchSubview.category==="hit"?ret>=tgt:ret<tgt; }
    }));
  },[rangedTrades,benchSubview,FLAT_TARGET,CALL_TARGET,PUT_TARGET]);

  const canSave=form.ticker.trim()&&form.entryPrice;

  async function handleSave(){
    if(saving) return;
    setSaving("saving");
    try{
      const ep=Number(form.entryPrice),xp=Number(form.exitPrice)||0,c=Math.max(1,Math.floor(Number(form.contracts)||1));
      const {returnPct,pnl}=calcPnl(ep,xp,c);
      const tradeData={...form,ticker:form.ticker.toUpperCase().trim(),entryDate:fmtDate(form.entryDate),exitDate:fmtDate(form.exitDate),expiration:fmtDate(form.expiration),exitTime:form.exitTime||"",entryPrice:ep,exitPrice:xp,strike:Number(form.strike)||0,entryStock:Number(form.entryStock)||0,exitStock:Number(form.exitStock)||0,contracts:c,returnPct,pnl};
      if(editingUid){const updated=trades.map(t=>t.uid===editingUid?{...t,...tradeData,updatedAt:Date.now()}:t);setTrades(updated);await saveTrades(ensureFields(updated));setSaveStatus("synced");}
      else{const result=await addTrade(tradeData);setTrades(prev=>[result.trade,...prev]);setSaveStatus(result.synced?"synced":"local");}
      setSaving("saved");
      setTimeout(()=>{setSaving(false);setSaveStatus(null);setForm(emptyForm);setEditingUid(null);setActiveTab("trades");},600);
    }catch(e){
      console.warn("Save failed:",e.message);
      setSaving(false);
    }
  }

  function handleEdit(trade){
    setForm({ticker:trade.ticker||"",entryDate:fmtDate(trade.entryDate),exitDate:fmtDate(trade.exitDate),entryTime:trade.entryTime||"",exitTime:trade.exitTime||"",strike:String(trade.strike||""),type:trade.type||"Call",expiration:fmtDate(trade.expiration),entryPrice:String(trade.entryPrice||""),exitPrice:String(trade.exitPrice??"0"),entryStock:String(trade.entryStock||""),exitStock:String(trade.exitStock||""),contracts:String(trade.contracts||1),notes:trade.notes||""});
    setEditingUid(trade.uid);setActiveTab("log");
  }

  async function handleDelete(uid){const result=await deleteTrade(uid);setTrades(prev=>prev.filter(t=>t.uid!==uid));setSaveStatus(result.synced?"synced":"local");setTimeout(()=>setSaveStatus(null),2500);}
  async function handleSync(){setSyncing(true);await loadTradesFromDB();setSyncing(false);setSaveStatus("local");setTimeout(()=>setSaveStatus(null),2500);}

  const tickerStats=useMemo(()=>{
    const map={};rangedTrades.forEach(t=>{if(!map[t.ticker])map[t.ticker]={trades:[],pnl:0};map[t.ticker].trades.push(t);map[t.ticker].pnl+=t.pnl;});
    return Object.entries(map).map(([ticker,d])=>({ticker,count:d.trades.length,winRate:((d.trades.filter(t=>t.pnl>0).length/d.trades.length)*100).toFixed(0),pnl:d.pnl.toFixed(2)})).sort((a,b)=>parseFloat(b.pnl)-parseFloat(a.pnl));
  },[rangedTrades]);

  const benchmarks=useMemo(()=>{
    const flat={hit:0,early:0,loss:0},tiered={hit:0,early:0,loss:0};
    rangedTrades.forEach(t=>{const tgt=t.type==="Call"?CALL_TARGET:PUT_TARGET;if(t.pnl<=0){flat.loss++;tiered.loss++;}else{if(t.returnPct>=FLAT_TARGET)flat.hit++;else flat.early++;if(t.returnPct>=tgt)tiered.hit++;else tiered.early++;}});
    return {flat,tiered};
  },[rangedTrades,FLAT_TARGET,CALL_TARGET,PUT_TARGET]);

  const pad=m?"16px 16px":"32px 40px";
  const inputStyle={background:"#0d1117",border:"1px solid #21262d",borderRadius:10,color:"#e6edf3",padding:m?"14px 16px":"12px 14px",fontSize:m?16:14,width:"100%",maxWidth:"100%",boxSizing:"border-box",outline:"none",fontFamily:"'IBM Plex Mono',monospace",overflow:"hidden"};
  const labelStyle={color:"#8b949e",fontSize:m?11:10,fontWeight:600,letterSpacing:0.8,textTransform:"uppercase",marginBottom:6,display:"block",fontFamily:"'Space Grotesk',sans-serif"};

  const formPreview=useMemo(()=>{const ep=Number(form.entryPrice),xp=Number(form.exitPrice);if(!ep)return null;return calcPnl(ep,xp||0,form.contracts);},[form.entryPrice,form.exitPrice,form.contracts]);

  function renderTradeList(tradeList,showFilters){
    const totalPages = Math.max(1, Math.ceil(tradeList.length / PER_PAGE));
    const safePage = Math.min(tradePage, totalPages - 1);
    const paged = tradeList.slice(safePage * PER_PAGE, (safePage + 1) * PER_PAGE);
    const showPagination = tradeList.length > PER_PAGE;
    return (<div>
      {showFilters && <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}><span style={{fontSize:11,color:"#8b949e",letterSpacing:1,flexShrink:0,marginRight:2}}>TICKER:</span>
          {(()=>{
            const MAX_VISIBLE = 8; // ALL + 7 tickers
            const hasMore = tickers.length > MAX_VISIBLE;
            const visible = filterTickerExpanded ? tickers : tickers.slice(0, MAX_VISIBLE);
            return <>
              {visible.map(t=><button key={t} className={`filter-btn ${filterTicker===t?"active":""}`} onClick={()=>setFilterTicker(t)}>{t}</button>)}
              {hasMore&&<span onClick={()=>setFilterTickerExpanded(!filterTickerExpanded)} style={{color:"#484f58",fontSize:10,cursor:"pointer",userSelect:"none",padding:"6px 4px",transition:"color 0.15s",whiteSpace:"nowrap"}} onMouseEnter={e=>e.currentTarget.style.color="#8b949e"} onMouseLeave={e=>e.currentTarget.style.color="#484f58"}>{filterTickerExpanded?"▴ less":`+${tickers.length - MAX_VISIBLE} more`}</span>}
            </>;
          })()}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}><span style={{fontSize:11,color:"#8b949e",letterSpacing:1,flexShrink:0,marginRight:2}}>TYPE:</span>{["ALL","Call","Put"].map(t=><button key={t} className={`filter-btn ${filterType===t?"active":""}`} onClick={()=>setFilterType(t)}>{t}</button>)}</div>
      </div>}
      <div style={{background:"#0d1117",border:"1px solid #21262d",borderRadius:10,overflow:"hidden"}}>
        {paged.map(t=><TradeCard key={t.uid||`${t.ticker}-${t.entryDate}-${t.entryTime}`} trade={t} L={L} onDelete={()=>handleDelete(t.uid)} onEdit={handleEdit}/>)}
        {tradeList.length===0&&<div style={{padding:30,textAlign:"center",color:"#8b949e",fontSize:12}}>No trades found.</div>}
      </div>
      <div style={{marginTop:12,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:11,color:"#8b949e"}}>{tradeList.length} trade{tradeList.length!==1?"s":""} · Newest first</div>
        {showPagination&&<div style={{display:"flex",alignItems:"center",gap:m?6:6}}>
          <button onClick={()=>goToPage(Math.max(0,safePage-1))} disabled={safePage===0} style={{background:safePage===0?"none":"#0d111722",border:`1px solid ${safePage===0?"#21262d":"#30363d"}`,borderRadius:6,color:safePage===0?"#30363d":"#8b949e",padding:m?"8px 12px":"6px 10px",fontFamily:"'Space Grotesk',sans-serif",fontSize:m?13:11,fontWeight:600,cursor:safePage===0?"default":"pointer",transition:"all 0.15s"}}>←</button>
          {(()=>{
            const pages=[];
            let lastWasDots=false;
            for(let p=0;p<totalPages;p++){
              if(p===0||p===totalPages-1||Math.abs(p-safePage)<=1){
                pages.push(<button key={p} onClick={()=>goToPage(p)} style={{background:p===safePage?"#58a6ff":"#0d111744",border:`1px solid ${p===safePage?"#58a6ff":"#30363d"}`,borderRadius:6,color:p===safePage?"#010409":"#c9d1d9",padding:m?"8px 12px":"6px 10px",fontFamily:"'Space Grotesk',sans-serif",fontSize:m?13:11,fontWeight:p===safePage?700:500,cursor:"pointer",minWidth:m?36:30,transition:"all 0.15s"}}>{p+1}</button>);
                lastWasDots=false;
              } else if(!lastWasDots){
                pages.push(<span key={`d${p}`} style={{color:"#8b949e",fontSize:12,padding:"0 3px",letterSpacing:2,fontWeight:600}}>···</span>);
                lastWasDots=true;
              }
            }
            return pages;
          })()}
          <button onClick={()=>goToPage(Math.min(totalPages-1,safePage+1))} disabled={safePage>=totalPages-1} style={{background:safePage>=totalPages-1?"none":"#0d111722",border:`1px solid ${safePage>=totalPages-1?"#21262d":"#30363d"}`,borderRadius:6,color:safePage>=totalPages-1?"#30363d":"#8b949e",padding:m?"8px 12px":"6px 10px",fontFamily:"'Space Grotesk',sans-serif",fontSize:m?13:11,fontWeight:600,cursor:safePage>=totalPages-1?"default":"pointer",transition:"all 0.15s"}}>→</button>
        </div>}
      </div>
    </div>);
  }

  if (!unlocked) return <LockScreen onUnlock={()=>setUnlocked(true)} />;

  return (
    <div style={{fontFamily:"'IBM Plex Mono','Courier New',monospace",background:"#010409",minHeight:"100dvh",color:"#e6edf3",padding:0,paddingBottom:m?110:0}}>
      <style>{`${m?"html,body,*{-ms-overflow-style:none;scrollbar-width:none}::-webkit-scrollbar{display:none}.tab-bar-wrap::before{content:'';position:absolute;top:-32px;left:0;right:0;height:32px;background:linear-gradient(to bottom,transparent,#010409);pointer-events:none}input[type=date]{-webkit-appearance:none;appearance:none;max-width:100%;color-scheme:dark}input[type=date]::-webkit-date-and-time-value{text-align:left}":"::-webkit-scrollbar{width:6px}::-webkit-scrollbar-track{background:#010409}::-webkit-scrollbar-thumb{background:#30363d;border-radius:3px}"}input:focus,select:focus,textarea:focus{border-color:#58a6ff !important}*{-webkit-tap-highlight-color:transparent;box-sizing:border-box}html,body{background-color:#010409 !important}.stat-card{background:#0d1117;border:1px solid #21262d;border-radius:${m?"10px":"12px"};padding:${m?"14px 16px":"22px 28px"}}.filter-btn{background:none;border:1px solid #30363d;border-radius:6px;color:#8b949e;font-family:inherit;font-size:${m?"12px":"11px"};font-weight:600;padding:${m?"8px 14px":"6px 12px"};cursor:pointer;letter-spacing:0.5px;white-space:nowrap}.filter-btn.active{background:#58a6ff22;border-color:#58a6ff;color:#58a6ff}@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}`}</style>

      <div style={{position:"sticky",top:0,zIndex:90,transform:m?`translateY(${headerVisible?0:-100}%)`:undefined,transition:m?"transform 0.35s ease":undefined}}>
      <div style={{background:"#0d1117",borderBottom:"1px solid #21262d",padding:m?"16px 18px":"20px 40px",display:"flex",alignItems:"center",justifyContent:"space-between",paddingTop:m?"max(16px,env(safe-area-inset-top))":"20px"}}>
        <div style={{display:"flex",alignItems:"center",gap:m?10:12}}>
          <div style={{width:m?34:38,height:m?34:38,borderRadius:9,border:"1.5px solid #21262d",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <svg width={m?18:20} height={m?18:20} viewBox="0 0 28 24" fill="none" strokeLinecap="round">
              <line x1="6" y1="1" x2="6" y2="23" stroke="#00e5a088" strokeWidth="1.2"/>
              <rect x="3.5" y="5" width="5" height="9" rx="1" fill="#00e5a0" stroke="#00e5a066" strokeWidth="0.5"/>
              <line x1="14" y1="3" x2="14" y2="19" stroke="#ff5f5f88" strokeWidth="1.2"/>
              <rect x="11.5" y="7" width="5" height="7" rx="1" fill="#ff5f5f" stroke="#ff5f5f66" strokeWidth="0.5"/>
              <line x1="22" y1="2" x2="22" y2="22" stroke="#00e5a088" strokeWidth="1.2"/>
              <rect x="19.5" y="9" width="5" height="6" rx="1" fill="#00e5a0" stroke="#00e5a066" strokeWidth="0.5"/>
            </svg>
          </div>
          <div style={{fontFamily:"'Plus Jakarta Sans','Space Grotesk',sans-serif",fontSize:m?16:18,fontWeight:800,color:"#e6edf3",letterSpacing:-0.4}}>StrikeBook <span style={{fontWeight:600,color:"#484f58",marginLeft:4}}>Demo</span></div>
        </div>
        <div style={{display:"flex",gap:m?10:12,alignItems:"center"}}>
          {saveStatus==="synced"&&<span style={{color:"#00e5a0",fontSize:10,fontWeight:600}}>✓ synced</span>}
          {saveStatus==="local"&&<span style={{color:"#f5c542",fontSize:10,fontWeight:600}}>✓ saved</span>}
          {!m&&<span style={{fontSize:11,color:"#30363d",fontWeight:500}}>{allTrades.length} trades</span>}
          <div onClick={handleSync} style={{width:8,height:8,borderRadius:"50%",cursor:"pointer",background:syncing?"#f5c542":online?"#00e5a0":"#ff5f5f",animation:syncing?"spin 1s linear infinite":"none",flexShrink:0,boxShadow:`0 0 6px ${syncing?"#f5c54266":online?"#00e5a044":"#ff5f5f44"}`}} title={online?"Online — tap to sync":"Offline"}/>
        </div>
      </div>

      {!m&&<div style={{background:"#0d1117",borderBottom:"1px solid #21262d",padding:"0 40px",display:"flex",gap:4}}>
        {[["dashboard","DASHBOARD"],["trades","TRADE LOG"],["log",editingUid?"EDIT TRADE":"NEW TRADE"]].map(([id,label])=>
          <button key={id} onClick={()=>{setActiveTab(id);setTickerSubview(null);setBenchSubview(null);}} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,letterSpacing:1,padding:"10px 18px",color:activeTab===id?"#58a6ff":"#8b949e",borderBottom:`2px solid ${activeTab===id?"#58a6ff":"transparent"}`}}>{label}</button>
        )}
      </div>}
      </div>{/* end sticky header */}

      <div style={{padding:pad,maxWidth:L.maxW,margin:m?"0":"0 auto"}}>

        {(tickerSubview||benchSubview)&&activeTab==="dashboard" ? (
          <div>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
              <button onClick={()=>{setTickerSubview(null);setBenchSubview(null);}} style={{background:"#21262d",border:"1px solid #30363d",borderRadius:8,color:"#e6edf3",fontFamily:"'Space Grotesk',sans-serif",fontSize:13,fontWeight:600,padding:m?"10px 16px":"8px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:6}}><span style={{fontSize:16}}>←</span> Back</button>
              {tickerSubview&&<><div style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:m?18:20,fontWeight:700,color:"#e6edf3"}}>{tickerSubview}</div><div style={{fontSize:11,color:"#8b949e"}}>{tickerSubTrades.length} trade{tickerSubTrades.length!==1?"s":""}{dashRange!=="ALL"?` · ${dashRange}`:""}</div></>}
              {benchSubview&&<><div style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:m?18:20,fontWeight:700,color:benchSubview.color}}>{benchSubview.category.toUpperCase()}</div><div style={{fontSize:11,color:"#8b949e"}}>{benchSubTrades.length} trade{benchSubTrades.length!==1?"s":""} · {benchSubview.bmType==="flat"?`Flat ${FLAT_TARGET}%`:"Tiered"}{dashRange!=="ALL"?` · ${dashRange}`:""}</div></>}
            </div>
            {tickerSubview&&renderTradeList(tickerSubTrades,false)}
            {benchSubview&&renderTradeList(benchSubTrades,false)}
          </div>
        ) : (<>

        {activeTab==="dashboard"&&(
          <div>
            {/* Range selector */}
            <div style={{display:"flex",alignItems:"center",justifyContent:m?"center":"flex-end",marginBottom:m?14:20,gap:m?0:10}}>
              {!m&&<span style={{fontSize:10,color:"#8b949e55",letterSpacing:1.5,fontWeight:600}}>RANGE</span>}
              <div style={{display:"flex",background:"#0d1117",border:"1px solid #21262d",borderRadius:8,overflow:"hidden",width:m?"100%":"auto"}}>
                {RANGE_OPTIONS.map(r=>{
                  const active=dashRange===r.key;
                  return <button key={r.key} onClick={()=>{setDashRange(r.key);saveDashRangeRemote(r.key);setTickerExpanded(false);}}
                    style={{
                      flex:m?1:"none",
                      background:active?"#58a6ff":"transparent",
                      color:active?"#010409":"#8b949e",
                      border:"none",
                      padding:m?"10px 0":"7px 16px",
                      fontFamily:"'Space Grotesk',sans-serif",
                      fontSize:m?12:11,
                      fontWeight:active?700:600,
                      letterSpacing:0.5,
                      cursor:"pointer",
                      transition:"all 0.15s ease",
                      borderRight:r.key!=="ALL"?"1px solid #21262d22":"none",
                    }}>{r.label}</button>;
                })}
              </div>
            </div>
            {dashRange!=="ALL"&&<div style={{fontSize:m?10:11,color:"#8b949e",marginBottom:m?10:14,textAlign:m?"center":"right"}}>Showing {rangedTrades.length} of {allTrades.length} trades</div>}
            <div style={{display:"grid",gridTemplateColumns:`repeat(${L.statCols},1fr)`,gap:m?10:18,marginBottom:m?14:28}}>
              {[{label:"TOTAL P&L",value:`${Number(metrics.totalPnl)>=0?"+":"-"}$${addCommas(Math.abs(Number(metrics.totalPnl)).toFixed(2))}`,color:Number(metrics.totalPnl)>=0?"#00e5a0":"#ff5f5f"},
                {label:"WIN RATE",value:`${metrics.winRate}%`,color:"#58a6ff"},
                {label:"AVG RETURN",value:`${Number(metrics.avgReturn)>=0?"+":""}${metrics.avgReturn}%`,color:"#e6edf3"},
                {label:"TOTAL TRADES",value:addCommas(String(metrics.total)),color:"#e6edf3"}
              ].map(s=><div key={s.label} className="stat-card"><div style={{color:"#8b949e",fontSize:m?9:11,fontWeight:600,letterSpacing:1.2,marginBottom:m?4:8}}>{s.label}</div><div style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:m?24:34,fontWeight:700,color:s.color}}>{s.value}</div></div>)}
            </div>

            <div style={{display:"grid",gridTemplateColumns:`repeat(${L.benchCols},1fr)`,gap:m?10:18,marginBottom:m?14:28}}>
              {[{key:"flat",title:`FLAT ${FLAT_TARGET}% TARGET`,data:benchmarks.flat},{key:"tiered",title:`TIERED — CALLS ${CALL_TARGET}% / PUTS ${PUT_TARGET}%`,data:benchmarks.tiered}].map(bm=>(
                <div key={bm.key} className="stat-card" style={{position:"relative"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:m?12:16}}>
                    <div style={{color:"#8b949e",fontSize:m?10:11,fontWeight:600,letterSpacing:1.2}}>{bm.title}</div>
                    <button onClick={(e)=>{e.stopPropagation();if(editingBench===bm.key){setEditingBench(null);}else{setEditingBench(bm.key);setBenchDraft(bm.key==="flat"?{flat:String(FLAT_TARGET)}:{call:String(CALL_TARGET),put:String(PUT_TARGET)});}}} style={{background:"none",border:"none",cursor:"pointer",padding:4,opacity:editingBench===bm.key?1:0.4,transition:"opacity 0.15s",display:"flex",alignItems:"center"}}
                      onMouseEnter={e=>e.currentTarget.style.opacity="0.8"} onMouseLeave={e=>e.currentTarget.style.opacity=editingBench===bm.key?"1":"0.4"}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={editingBench===bm.key?"#58a6ff":"#8b949e"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    </button>
                  </div>

                  {/* Inline settings editor */}
                  {editingBench===bm.key && (
                    <div style={{background:"#010409",border:"1px solid #21262d",borderRadius:8,padding:m?"12px":"14px 16px",marginBottom:m?12:16}}>
                      <div style={{display:"flex",gap:m?8:12,alignItems:"flex-end",flexWrap:"wrap"}}>
                        {bm.key==="flat" ? (
                          <div style={{flex:1,minWidth:80}}>
                            <label style={{color:"#8b949e",fontSize:9,fontWeight:600,letterSpacing:1,display:"block",marginBottom:4}}>TARGET %</label>
                            <input type="text" inputMode="decimal" value={benchDraft.flat||""} onChange={e=>setBenchDraft({flat:e.target.value})}
                              style={{background:"#0d1117",border:"1px solid #30363d",borderRadius:6,color:"#e6edf3",padding:"8px 10px",fontSize:14,width:"100%",boxSizing:"border-box",outline:"none",fontFamily:"'Space Grotesk',sans-serif"}} />
                          </div>
                        ) : (<>
                          <div style={{flex:1,minWidth:80}}>
                            <label style={{color:"#58a6ff",fontSize:9,fontWeight:600,letterSpacing:1,display:"block",marginBottom:4}}>CALLS %</label>
                            <input type="text" inputMode="decimal" value={benchDraft.call||""} onChange={e=>setBenchDraft(d=>({...d,call:e.target.value}))}
                              style={{background:"#0d1117",border:"1px solid #30363d",borderRadius:6,color:"#e6edf3",padding:"8px 10px",fontSize:14,width:"100%",boxSizing:"border-box",outline:"none",fontFamily:"'Space Grotesk',sans-serif"}} />
                          </div>
                          <div style={{flex:1,minWidth:80}}>
                            <label style={{color:"#c084fc",fontSize:9,fontWeight:600,letterSpacing:1,display:"block",marginBottom:4}}>PUTS %</label>
                            <input type="text" inputMode="decimal" value={benchDraft.put||""} onChange={e=>setBenchDraft(d=>({...d,put:e.target.value}))}
                              style={{background:"#0d1117",border:"1px solid #30363d",borderRadius:6,color:"#e6edf3",padding:"8px 10px",fontSize:14,width:"100%",boxSizing:"border-box",outline:"none",fontFamily:"'Space Grotesk',sans-serif"}} />
                          </div>
                        </>)}
                        <button onClick={()=>{
                          const updated = {...benchSettings};
                          if(bm.key==="flat"){ const v=parseFloat(benchDraft.flat); if(v>0&&v<=100) updated.flat=v; }
                          else { const c=parseFloat(benchDraft.call),p=parseFloat(benchDraft.put); if(c>0&&c<=100) updated.call=c; if(p>0&&p<=100) updated.put=p; }
                          setBenchSettings(updated); saveBenchmarkSettingsRemote(updated); setEditingBench(null);
                        }} style={{background:"#58a6ff",color:"#010409",border:"none",borderRadius:6,padding:"8px 14px",fontFamily:"'Space Grotesk',sans-serif",fontSize:12,fontWeight:700,cursor:"pointer",letterSpacing:0.5,transition:"background 0.15s"}}>SAVE</button>
                        <button onClick={()=>setEditingBench(null)} style={{background:"none",border:"1px solid #30363d",borderRadius:6,color:"#8b949e",padding:"8px 14px",fontFamily:"'Space Grotesk',sans-serif",fontSize:12,fontWeight:600,cursor:"pointer"}}>CANCEL</button>
                      </div>
                    </div>
                  )}

                  <div style={{display:"flex",gap:m?6:10}}>
                    {[["hit","HIT",bm.data.hit,"#00e5a0"],["early","EARLY",bm.data.early,"#f5c542"],["loss","LOSS",bm.data.loss,"#ff5f5f"]].map(([cat,l,v,c])=>
                      <div key={cat} onClick={()=>{setTickerSubview(null);setBenchSubview({bmType:bm.key,category:cat,color:c});}} style={{flex:1,background:c+"11",border:`1px solid ${c}33`,borderRadius:m?6:8,padding:m?"10px 6px":"14px 10px",textAlign:"center",cursor:"pointer",transition:"border-color 0.15s"}} onMouseEnter={e=>e.currentTarget.style.borderColor=c+"88"} onMouseLeave={e=>e.currentTarget.style.borderColor=c+"33"}>
                        <div style={{fontSize:m?20:26,fontWeight:700,color:c,fontFamily:"'Space Grotesk',sans-serif"}}>{v}</div>
                        <div style={{fontSize:m?9:10,color:c+"bb",letterSpacing:1,marginTop:m?2:4}}>{l}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="stat-card" style={{marginBottom:m?10:18}}>
              <div style={{color:"#8b949e",fontSize:m?10:11,fontWeight:600,letterSpacing:1.2,marginBottom:m?14:18}}>PERFORMANCE BY TICKER — TAP FOR DETAILS</div>
              {(()=>{
                const maxVisible = m ? 6 : L.tickerCols;
                const hasMore = tickerStats.length > maxVisible;
                const visible = tickerExpanded ? tickerStats : tickerStats.slice(0, maxVisible);
                return <>
                  <div style={{display:"grid",gridTemplateColumns:`repeat(${L.tickerCols},1fr)`,gap:m?10:14}}>
                    {visible.map(t=>(
                      <div key={t.ticker} onClick={()=>setTickerSubview(t.ticker)} style={{background:"#010409",border:`1px solid ${parseFloat(t.pnl)>=0?"#00e5a022":"#ff5f5f22"}`,borderRadius:m?8:10,padding:m?"12px 14px":"16px 18px",cursor:"pointer",transition:"border-color 0.15s"}} onMouseEnter={e=>e.currentTarget.style.borderColor=parseFloat(t.pnl)>=0?"#00e5a055":"#ff5f5f55"} onMouseLeave={e=>e.currentTarget.style.borderColor=parseFloat(t.pnl)>=0?"#00e5a022":"#ff5f5f22"}>
                        <div style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:m?16:18,fontWeight:700,color:"#e6edf3",marginBottom:m?6:8}}>{t.ticker}</div>
                        <div style={{fontSize:m?20:24,fontWeight:700,color:parseFloat(t.pnl)>=0?"#00e5a0":"#ff5f5f",fontFamily:"'Space Grotesk',sans-serif"}}>{parseFloat(t.pnl)>=0?"+$":"-$"}{fmtMoney(t.pnl)}</div>
                        <div style={{fontSize:m?10:11,color:"#8b949e",marginTop:m?4:6}}>{t.count} trade{t.count!==1?"s":""} · {t.winRate}% WR</div>
                      </div>
                    ))}
                  </div>
                  {hasMore&&<div onClick={()=>setTickerExpanded(!tickerExpanded)} style={{textAlign:"center",paddingTop:m?10:12,cursor:"pointer",userSelect:"none"}}>
                    <span style={{color:"#484f58",fontSize:10,letterSpacing:0.3,transition:"color 0.15s"}} onMouseEnter={e=>e.currentTarget.style.color="#8b949e"} onMouseLeave={e=>e.currentTarget.style.color="#484f58"}>{tickerExpanded?`▴ show less`:`▾ ${tickerStats.length - maxVisible} more`}</span>
                  </div>}
                </>;
              })()}
            </div>

            <div style={{display:"grid",gridTemplateColumns:`repeat(${L.cpCols},1fr)`,gap:m?10:18}}>
              {["Call","Put"].map(type=>{const t=rangedTrades.filter(x=>x.type===type);const mt=calcMetrics(t);const color=type==="Call"?"#58a6ff":"#c084fc";
                return <div key={type} className="stat-card" style={{borderColor:color+"44"}}>
                  <div style={{color,fontSize:m?10:11,fontWeight:600,letterSpacing:1.2,marginBottom:m?8:12}}>{type.toUpperCase()}S — {t.length} TRADES</div>
                  <div style={{display:"flex",gap:m?16:28,marginTop:m?8:12}}>
                    {[["WIN RATE",mt.winRate+"%"],["AVG RETURN",`${Number(mt.avgReturn)>=0?"+":""}${mt.avgReturn}%`],["TOTAL P&L",`${Number(mt.totalPnl)>=0?"+$":"-$"}${fmtMoney(mt.totalPnl)}`]].map(([l,v])=>
                      <div key={l}><div style={{fontSize:m?10:11,color:"#8b949e",letterSpacing:1}}>{l}</div><div style={{fontSize:m?16:22,fontWeight:700,color,fontFamily:"'Space Grotesk',sans-serif",marginTop:m?2:4}}>{v}</div></div>
                    )}
                  </div>
                </div>;
              })}
            </div>
          </div>
        )}

        {activeTab==="trades"&&renderTradeList(filtered,true)}

        {activeTab==="log"&&(
          <div style={{maxWidth:m?620:720}}>
            <div style={{fontFamily:"'Plus Jakarta Sans','Space Grotesk',sans-serif",fontSize:m?20:18,fontWeight:800,color:"#e6edf3",marginBottom:4,letterSpacing:-0.3}}>{editingUid?"Edit Trade":"Log New Trade"}</div>
            <div style={{fontSize:m?12:11,color:"#484f58",marginBottom:m?24:28}}>{editingUid?"Update fields below. P&L recalculates automatically.":"P&L and return % calculated automatically."}</div>

            {/* Ticker + Type row */}
            <div style={{display:"grid",gridTemplateColumns:m?"1fr auto":"2fr 1fr",gap:m?12:14,marginBottom:m?24:28}}>
              <div><label style={labelStyle}>TICKER</label><input type="text" placeholder="e.g. AAPL" value={form.ticker} onChange={e=>setForm(f=>({...f,ticker:e.target.value}))} style={inputStyle}/></div>
              <div><label style={labelStyle}>TYPE</label><select value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))} style={{...inputStyle,minWidth:m?100:120}}><option>Call</option><option>Put</option></select></div>
            </div>

            {/* Section: Dates */}
            <div style={{fontFamily:"'Plus Jakarta Sans','Space Grotesk',sans-serif",fontSize:m?11:10,color:"#58a6ff",letterSpacing:1.5,fontWeight:700,marginBottom:m?12:14,opacity:0.6}}>DATES</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:m?10:14,marginBottom:m?8:10}}>
              <div><label style={labelStyle}>ENTRY</label><input type="date" value={form.entryDate} onChange={e=>setForm(f=>({...f,entryDate:e.target.value}))} style={{...inputStyle,height:m?50:44,lineHeight:"normal"}}/></div>
              <div><label style={labelStyle}>EXIT</label><input type="date" value={form.exitDate} onChange={e=>setForm(f=>({...f,exitDate:e.target.value}))} style={{...inputStyle,height:m?50:44,lineHeight:"normal"}}/></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:m?10:14,marginBottom:m?28:32}}>
              <div><label style={labelStyle}>ENTRY TIME</label><input type="text" placeholder="e.g. 2:30PM" value={form.entryTime} onChange={e=>setForm(f=>({...f,entryTime:e.target.value}))} style={inputStyle}/></div>
              <div><label style={labelStyle}>EXIT TIME</label><input type="text" placeholder="e.g. 3:15PM" value={form.exitTime} onChange={e=>setForm(f=>({...f,exitTime:e.target.value}))} style={inputStyle}/></div>
            </div>

            {/* Section: Contract */}
            <div style={{fontFamily:"'Plus Jakarta Sans','Space Grotesk',sans-serif",fontSize:m?11:10,color:"#58a6ff",letterSpacing:1.5,fontWeight:700,marginBottom:m?12:14,opacity:0.6}}>CONTRACT</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:m?10:14,marginBottom:m?8:10}}>
              <div><label style={labelStyle}>STRIKE</label><input type="text" inputMode="decimal" placeholder="e.g. 400" value={form.strike} onChange={e=>setForm(f=>({...f,strike:e.target.value}))} style={inputStyle}/></div>
              <div><label style={labelStyle}>EXPIRATION</label><input type="date" value={form.expiration} onChange={e=>setForm(f=>({...f,expiration:e.target.value}))} style={{...inputStyle,height:m?50:44,lineHeight:"normal"}}/></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:m?10:14,marginBottom:m?12:14}}>
              <div><label style={labelStyle}>CONTRACTS</label><input type="text" inputMode="decimal" placeholder="1" value={form.contracts} onChange={e=>setForm(f=>({...f,contracts:e.target.value}))} style={inputStyle}/></div>
              <div style={{display:"flex",alignItems:"flex-end"}}>
                <button onClick={()=>setAdvancedOpen(!advancedOpen)} style={{
                  width:"100%",height:m?50:44,
                  background:advancedOpen?"#58a6ff11":"#0d1117",
                  border:`1px dashed ${advancedOpen?"#58a6ff44":"#21262d"}`,
                  borderRadius:10,
                  color:advancedOpen?"#58a6ff":"#484f58",
                  fontFamily:"'Space Grotesk',sans-serif",
                  fontSize:m?12:11,fontWeight:600,letterSpacing:0.5,
                  cursor:"pointer",
                  transition:"all 0.2s ease",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0.7}}>
                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  Advanced
                </button>
              </div>
            </div>
            {advancedOpen&&<div style={{
              background:"#0d1117",border:"1px solid #21262d",borderRadius:12,
              padding:m?"20px 18px":"24px 22px",marginBottom:m?28:32,
              animation:"fadeIn 0.2s ease",
            }}>
              <div style={{textAlign:"center",color:"#30363d",fontSize:m?13:12,fontFamily:"'Space Grotesk',sans-serif",fontWeight:500,padding:m?"20px 0":"24px 0"}}>
                Advanced data coming soon
              </div>
            </div>}
            {!advancedOpen&&<div style={{marginBottom:m?16:20}}/>}

            {/* Section: Premiums */}
            <div style={{fontFamily:"'Plus Jakarta Sans','Space Grotesk',sans-serif",fontSize:m?11:10,color:"#58a6ff",letterSpacing:1.5,fontWeight:700,marginBottom:m?12:14,opacity:0.6}}>PREMIUMS</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:m?10:14,marginBottom:m?28:32}}>
              <div><label style={labelStyle}>ENTRY</label><input type="text" inputMode="decimal" placeholder="e.g. 0.35" value={form.entryPrice} onChange={e=>setForm(f=>({...f,entryPrice:e.target.value}))} style={inputStyle}/></div>
              <div><label style={labelStyle}>EXIT</label><input type="text" inputMode="decimal" placeholder="e.g. 0.48" value={form.exitPrice} onChange={e=>setForm(f=>({...f,exitPrice:e.target.value}))} style={inputStyle}/></div>
            </div>

            {/* Section: Stock Prices */}
            <div style={{fontFamily:"'Plus Jakarta Sans','Space Grotesk',sans-serif",fontSize:m?11:10,color:"#58a6ff",letterSpacing:1.5,fontWeight:700,marginBottom:m?12:14,opacity:0.6}}>STOCK PRICE</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:m?10:14,marginBottom:m?28:32}}>
              <div><label style={labelStyle}>ENTRY</label><input type="text" inputMode="decimal" placeholder="e.g. 397.25" value={form.entryStock} onChange={e=>setForm(f=>({...f,entryStock:e.target.value}))} style={inputStyle}/></div>
              <div><label style={labelStyle}>EXIT</label><input type="text" inputMode="decimal" placeholder="e.g. 401.55" value={form.exitStock} onChange={e=>setForm(f=>({...f,exitStock:e.target.value}))} style={inputStyle}/></div>
            </div>

            {/* Notes */}
            <div style={{marginBottom:m?20:18}}><label style={labelStyle}>NOTES</label><textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Why did you enter/exit?..." style={{...inputStyle,height:m?90:80,resize:"vertical"}}/></div>
            {formPreview&&<div style={{marginTop:16,background:"#0d1117",border:"1px solid #21262d",borderRadius:10,padding:"16px 18px"}}>
              <div style={{fontSize:10,color:"#8b949e",letterSpacing:1,marginBottom:12}}>LIVE PREVIEW{Number(form.contracts)>1?` (x${form.contracts} contracts)`:""}</div>
              <div style={{display:"flex",gap:24,alignItems:"flex-end",flexWrap:m?"wrap":"nowrap"}}>
                <div><div style={{fontSize:10,color:"#8b949e",marginBottom:2}}>RETURN</div><div style={{fontSize:26,fontWeight:700,color:pnlColor(formPreview.returnPct),fontFamily:"'Space Grotesk',sans-serif"}}>{fmtPct(formPreview.returnPct)}</div></div>
                <div><div style={{fontSize:10,color:"#8b949e",marginBottom:2}}>P&L</div><div style={{fontSize:26,fontWeight:700,color:pnlColor(formPreview.pnl),fontFamily:"'Space Grotesk',sans-serif"}}>{fmtPnl(formPreview.pnl)}</div></div>
                <div><div style={{fontSize:10,color:"#8b949e",marginBottom:2}}>{FLAT_TARGET}% TARGET</div><div style={{fontSize:14,fontWeight:700,color:formPreview.returnPct>=FLAT_TARGET?"#00e5a0":"#f5c542",fontFamily:"'Space Grotesk',sans-serif"}}>{formPreview.returnPct>=FLAT_TARGET?"✓ HIT":`${(FLAT_TARGET-formPreview.returnPct).toFixed(1)}% short`}</div></div>
                <div><div style={{fontSize:10,color:"#8b949e",marginBottom:2}}>TIERED {form.type==="Call"?`${CALL_TARGET}%`:`${PUT_TARGET}%`}</div><div style={{fontSize:14,fontWeight:700,color:formPreview.returnPct>=(form.type==="Call"?CALL_TARGET:PUT_TARGET)?"#00e5a0":"#f5c542",fontFamily:"'Space Grotesk',sans-serif"}}>{formPreview.returnPct>=(form.type==="Call"?CALL_TARGET:PUT_TARGET)?"✓ HIT":`${((form.type==="Call"?CALL_TARGET:PUT_TARGET)-formPreview.returnPct).toFixed(1)}% short`}</div></div>
              </div>
            </div>}
            <div style={{display:"flex",gap:10,marginTop:24}}>
              <button onClick={handleSave} disabled={!canSave||!!saving} style={{
                background:saving==="saved"?"#00e5a0":saving==="saving"?"#58a6ff88":!canSave?"#21262d":"#58a6ff",
                color:saving==="saved"?"#010409":!canSave?"#8b949e":"#010409",
                border:"none",borderRadius:8,padding:m?"14px 24px":"12px 20px",
                fontFamily:"'Space Grotesk',sans-serif",fontSize:m?14:13,fontWeight:700,letterSpacing:1,
                cursor:canSave&&!saving?"pointer":"default",
                transition:"all 0.2s ease",
                transform:saving==="saved"?"scale(1.02)":"scale(1)",
                minWidth:m?160:140,
              }}>{saving==="saving"?"SAVING...":saving==="saved"?"✓ SAVED":editingUid?"UPDATE TRADE":"SAVE TRADE"}</button>
              <button onClick={()=>{setForm(emptyForm);setEditingUid(null);setActiveTab(editingUid?"trades":"dashboard");}} disabled={!!saving} style={{background:"#21262d",color:"#8b949e",border:"1px solid #30363d",borderRadius:8,padding:m?"14px 24px":"12px 20px",fontFamily:"'Space Grotesk',sans-serif",fontSize:m?14:13,fontWeight:600,cursor:saving?"default":"pointer",transition:"opacity 0.15s",opacity:saving?0.5:1}}>CANCEL</button>
            </div>
          </div>
        )}
        </>)}
      </div>

      {m&&<>
        <div className="tab-bar-wrap" style={{position:"fixed",bottom:0,left:0,right:0,zIndex:100}}>
          <div style={{background:"rgba(13,17,23,0.85)",backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",borderTop:"1px solid #21262d44",display:"flex",justifyContent:"space-around",paddingBottom:"max(8px,env(safe-area-inset-bottom))",paddingTop:8}}>
          {[
            ["dashboard",<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,"Dashboard"],
            ["trades",<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg>,"Trades"],
            ["log",<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>,"New"]
          ].map(([id,icon,label])=>
            <button key={id} onClick={()=>{setActiveTab(id);setTickerSubview(null);setBenchSubview(null);if(id==="log"){setEditingUid(null);setForm(emptyForm);}}} style={{background:"none",border:"none",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4,color:activeTab===id?"#58a6ff":"#8b949e",fontFamily:"'Space Grotesk',sans-serif",fontSize:10,fontWeight:600,letterSpacing:0.8,padding:"4px 20px",opacity:activeTab===id?1:0.5,transition:"opacity 0.15s, color 0.15s"}}>{icon}<span>{label}</span></button>
          )}
        </div></div>
      </>}
    </div>
  );
}
